import { OFFLINE_ENABLED } from './entities';
import { isInitialSyncComplete } from './syncClient';
import { isConnectivityError, isEffectivelyOnline, reportConnectivitySuccess, reportFromError } from './connectivity';

/**
 * Wrap a React Query `queryFn` so it prefers the network but falls back to the
 * local replica when offline or when the network call fails. When the feature
 * flag is OFF, the original network fn is returned unchanged — zero behavior
 * change for the live app.
 *
 * B7: the replica is only served once the FIRST full pull has completed. Before
 * that the replica may be partial (an interrupted initial sync), so serving it
 * would present half the chart as if it were the whole record. Until complete,
 * surface the failure (the network error if we have one, else a typed
 * not-ready error) so React Query shows its error state rather than partial data.
 */
/** A gateway answering for a server that is down or restarting (a Render deploy, an outage). */
const UNREACHABLE_STATUSES = new Set([502, 503, 504]);

/**
 * COULD THE SERVER NOT BE REACHED? Only then may a read fall back to the replica (fix 30, sync-6).
 *
 * No response at all (wifi off, a dead API, a timeout) or a 502/503/504 leaves the question
 * unanswered, so the saved copy is the best answer there is. Every other status IS an answer: a
 * 403 refusal (INV-108: serving the replica would show what the server just refused), a 404 for a
 * record deleted on the server, a 500 bug, a paywall or a session prompt. Before this, any of those
 * quietly showed the saved copy while the app said it was online, so staff acted on old balances.
 */
export function isServerUnreachable(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
  return status === undefined ? isConnectivityError(error) : UNREACHABLE_STATUSES.has(status);
}

export function withOfflineFallback<T>(
  networkFn: () => Promise<T>,
  dexieFn: () => Promise<T>,
): () => Promise<T> {
  if (!OFFLINE_ENABLED) return networkFn;
  return async () => {
    let networkError: unknown;
    if (isEffectivelyOnline()) {
      try {
        const res = await networkFn();
        reportConnectivitySuccess();
        return res;
      } catch (e) {
        // A transport failure marks us observed-down; any response clears a down state a
        // CONCURRENT read recorded, since an answer proves the server is reachable.
        reportFromError(e);
        // AN ANSWER ENDS HERE (INV-108, fix 30): rethrown so React Query shows the error state
        // and the screen says "no access" or "something went wrong", never the saved copy.
        if (!isServerUnreachable(e)) throw e;
        networkError = e; // could not reach the server despite onLine — maybe fall back
      }
    }
    if (await isInitialSyncComplete()) {
      return dexieFn();
    }
    if (networkError !== undefined) throw networkError;
    throw new Error('offline-replica-not-ready');
  };
}

/**
 * `enabled` guard for NON-replicated queries (no offline fallback). When the
 * flag is ON, don't fire while EFFECTIVELY offline — navigator.onLine AND the
 * engine's observed reachability (virtual adapters keep onLine stale-true) —
 * avoiding doomed requests + error UI. When the flag is OFF this is ALWAYS
 * true, so the live app is completely unaffected by `navigator.onLine` quirks —
 * use this instead of a bare `navigator.onLine` in `enabled`.
 */
export const skipWhenOffline = (): boolean => !OFFLINE_ENABLED || isEffectivelyOnline();
